<script lang="ts">
  import {
    BlocksIcon,
    Clock3Icon,
    CodepenIcon,
    DatabaseBackupIcon,
    ExpandIcon,
    RecycleIcon,
    ZapIcon,
  } from "lucide-svelte";

  import CanOfSoupLogo from "$lib/assets/logos/customers/CanOfSoup.svg";
  import OpenPipeLogo from "$lib/assets/logos/customers/OpenPipe.svg";
  import RampLogo from "$lib/assets/logos/customers/Ramp.svg";
  import {
    type FeaturedExample,
    getFeaturedExamples,
  } from "$lib/docs/featured-examples";
  import ExampleCardLinkCarousel from "$lib/ui/marketing/ExampleCardLinkCarousel.svelte";
  import GetStartedOrBookDemo from "$lib/ui/marketing/GetStartedOrBookDemo.svelte";
  import PageHeader from "$lib/ui/marketing/PageHeader.svelte";
  import PageHeaderContent from "$lib/ui/marketing/PageHeaderContent.svelte";
  import UseCase from "$lib/ui/marketing/landing/UseCase.svelte";
  import UseCaseQuotes, { type Quote } from "../UseCaseQuotes.svelte";

  const examples: FeaturedExample[] = getFeaturedExamples(
    "use-case-finetuning",
  );

  const quotes: Quote[] = [
    {
      logo: CanOfSoupLogo,
      quote:
        "We fine-tune image models on Modal because we can experiment with new ideas quickly. We chose to deploy on Modal too because it's far more stable than any alternative solutions we found.",
      name: "Eric Meier",
      title: "Co-Founder",
      href: "https://www.canofsoup.com/",
    },
    {
      logo: OpenPipeLogo,
      quote:
        "Modal is the easiest way to experiment as we develop new fine-tuning techniques. We’ve been able to validate new features faster and beat competitors because of how quickly we can try new ideas.",
      name: "Kyle Corbitt",
      title: "CTO",
      href: "https://www.openpipe.ai/",
    },
    {
      logo: RampLogo,
      quote:
        "Modal's user-friendly interface and efficient tools have truly empowered our team to navigate data-intensive tasks with ease, enabling us to achieve our project goals more efficiently.",
      name: "Karim Atiyeh",
      title: "Co-Founder & CTO",
      href: "https://www.ramp.com/",
    },
  ];
</script>

<PageHeader>
  <PageHeaderContent>
    <span slot="title">Fine-tuning</span>
    <span slot="subtitle">
      Fine-tune open-source models on your own data with Modal's serverless
      GPUs.
    </span>
    <div slot="image">
      <img
        class="w-full h-full object-contain"
        src="https://modal-cdn.com/tmpk4zx_cyn_93cd21d9.webp"
        alt=""
        role="presentation"
        width="535"
        height="400"
      />
    </div>
    <div slot="cta" class="flex gap-x-4">
      <GetStartedOrBookDemo />
    </div>
  </PageHeaderContent>
</PageHeader>

<UseCaseQuotes {quotes} />

<div class="marketing-container">
  <UseCase
    titleContinued="Programmable, unlimited training "
    titleGreen="infrastructure"
    titleReversed
    details={[
      {
        icon: CodepenIcon,
        headline: "Define and share reproducible environments",
        sublineHtml:
          "Define your requirements in code and let your team run the same without having to set up a local environment.",
      },
      {
        icon: ZapIcon,
        headline: "Fast hyperparameter sweeps",
        sublineHtml:
          "Scale up to hundreds of multi-GPU fine-tuning runs in just a few seconds with a single function call.",
      },
      {
        icon: ExpandIcon,
        headline: "Flexible framework integration",
        sublineHtml:
          "Use your favorite ML fine-tuning frameworks, like Hugging Face, PyTorch, and Axolotl…or write your own training loop.",
      },
    ]}
    placement="left"
  >
    <div class="h-80 md:h-full flex flex-col justify-center">
      <img
        class="object-contain h-full"
        src="https://modal-cdn.com/use-case-tensor-rt.webp"
        alt=""
        role="presentation"
        width="1418"
        height="532"
      />
    </div>
  </UseCase>

  <UseCase
    titleContinued="Monitoring and "
    titleGreen="MLOps"
    titleReversed
    details={[
      {
        icon: BlocksIcon,
        headline: "Integration with popular tools",
        sublineHtml:
          "Monitor experiment results with Weights and Biases and visualize training progress using TensorBoard.",
      },
      {
        icon: Clock3Icon,
        headline: "Real-time resource metrics",
        sublineHtml:
          "Access detailed performance data from your Modal dashboard.",
      },
    ]}
    placement="right"
  >
    <div class="h-80 md:h-full flex flex-col justify-center">
      <img
        class="object-contain h-full"
        src="https://modal-cdn.com/use-case-active-resources.webp"
        alt=""
        role="presentation"
        width="1390"
        height="756"
      />
    </div>
  </UseCase>

  <UseCase
    titleContinued="Unified "
    titleGreen="infrastructure"
    titleReversed
    details={[
      {
        icon: DatabaseBackupIcon,
        headline: "Efficient data management",
        sublineHtml:
          "Store private datasets and model weights in Modal Volumes as easily as writing to local disk.",
      },
      {
        icon: RecycleIcon,
        headline: "End-to-end model lifecycle",
        sublineHtml:
          "Deploy your functions for data processing, fine-tuning, and serving, all on Modal.",
      },
    ]}
    placement="left"
  >
    <div class="h-80 md:h-full flex flex-col justify-center">
      <img
        class="object-contain h-full"
        src="https://modal-cdn.com/use-case-function-calls.webp"
        alt=""
        role="presentation"
        width="1396"
        height="948"
      />
    </div>
  </UseCase>
</div>

<ExampleCardLinkCarousel {examples} title="Try it out" />
